import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { LogOut, Menu, Send, Search } from "lucide-react";
import LogoutModal from "./LogoutModal";
import NotificationBell from "./NotificationBell";
import AdminBroadcastModal from "../admin/AdminBroadcastModal";
import GlobalSearchModal from "./GlobalSearchModal";
import PageRefreshButton from "./PageRefreshButton";

const AdminHeader = () => {
  const { user, logout } = useAuth();
  const { toggleAdminMobileSidebar } = useSidebar();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setShowLogoutModal(false);
    setShowUserDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className="product-topbar sticky top-0 z-30">
      <div className="px-3 py-2 sm:px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleAdminMobileSidebar}
              className="rounded-md p-1.5 text-ink-secondary hover:bg-surface-muted hover:text-ink lg:hidden"
            >
              <Menu className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-md bg-ink sm:h-7 sm:w-7">
                <span className="text-[11px] font-semibold tracking-tight text-white sm:text-xs">VL</span>
              </div>
              <span className="text-sm font-semibold text-ink sm:text-[15px]">
                vendaList
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => setShowSearchModal(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface px-2 text-ink-secondary transition-colors hover:bg-surface-muted hover:text-ink sm:px-2.5"
              title="Search (⌘K)"
              aria-label="Open search"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="hidden text-xs font-medium sm:inline">Search</span>
              <kbd className="ml-1 hidden rounded border border-border bg-surface-muted px-1 py-0.5 text-[10px] text-ink-tertiary md:inline">
                ⌘K
              </kbd>
            </button>

            <PageRefreshButton />

            <button
              onClick={() => setShowBroadcastModal(true)}
              className="flex items-center gap-1 rounded-md p-1.5 text-ink-secondary transition-colors hover:bg-surface-muted hover:text-ink"
              title="Send Broadcast"
            >
              <Send className="h-4 w-4" />
              <span className="hidden text-xs font-medium sm:inline">Broadcast</span>
            </button>

            {/* Notification Bell */}
            <NotificationBell />

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center space-x-1 sm:space-x-2 text-ink-secondary transition-colors hover:text-ink"
              >
                <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-ink">
                  <span className="text-xs sm:text-sm font-medium text-white">
                    {user?.name ? user.name.charAt(0).toUpperCase() : "A"}
                  </span>
                </div>
              </button>

              {showUserDropdown && (
                <div className="absolute right-0 z-[10001] mt-2 w-44 rounded-product-md border border-border bg-surface py-1 shadow-sm sm:w-48">
                  <div className="border-b border-border px-4 py-2">
                    <p className="text-sm font-medium text-ink">
                      {user?.name || "Admin"}
                    </p>
                    <p className="text-xs text-ink-secondary">
                      {user?.email || "admin@example.com"}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      setShowLogoutModal(true);
                    }}
                    className="flex w-full items-center px-4 py-2 text-sm text-ink transition-colors hover:bg-surface-muted"
                  >
                    <LogOut className="mr-3 h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />

      {/* Broadcast Modal */}
      <AdminBroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
      />

      <GlobalSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        variant="admin"
      />
    </header>
  );
};

export default AdminHeader;
